import React from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import axios from "../../utils/axios";

import { Button, CustomSelect, DatePickerField, DaySelectionPills, Select } from "../../components/ui";
import { useAsyncAction } from "../../hooks/useAsyncAction";
import {
  type CampaignFlowState,
  fetchCampaignDraft,
  draftSearchFromCampaignId,
  resolveFlowScope,
  resolveCampaignIdFromStateOrSearch,
  upsertCampaignDraft,
} from "./campaignDraftFlow";
import { CAMPAIGN_TIMEZONE_OPTIONS } from "./campaignFlow";
import { ownerSelector } from "../../store/store";

const buildHourlyOptions = () =>
  Array.from({ length: 24 }).map((_, hour) => {
    const label = `${String(hour).padStart(2, "0")}:00`;
    return { value: label, label };
  });

const HOUR_OPTIONS = buildHourlyOptions();
const DAY_OPTIONS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const ONE_DAY_MS = 24 * 60 * 60 * 1000;
const DAY_NAME_BY_INDEX = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"] as const;

const getTodayStart = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today;
};

function getTimeZoneOffsetMinutes(timezone: string, referenceDate: Date) {
  const utcDate = new Date(referenceDate.toLocaleString("en-US", { timeZone: "UTC" }));
  const zoneDate = new Date(referenceDate.toLocaleString("en-US", { timeZone: timezone }));
  const zoneAheadMinutes = Math.round((zoneDate.getTime() - utcDate.getTime()) / 60000);
  return -zoneAheadMinutes;
}

const parseLegacyTimeToHHMM = (value: unknown): string | null => {
  if (!value) return null;
  const date = new Date(value as any);
  if (Number.isNaN(date.getTime())) return null;
  const hh = String(date.getHours()).padStart(2, "0");
  const mm = String(date.getMinutes()).padStart(2, "0");
  return `${hh}:${mm}`;
};

const resolveTimezoneFromLegacyOffset = (offset: unknown, referenceDate: Date): string => {
  const parsed = Number(offset);
  if (!Number.isFinite(parsed)) return "Europe/London";
  for (const option of CAMPAIGN_TIMEZONE_OPTIONS) {
    const optionOffset = getTimeZoneOffsetMinutes(option.value, referenceDate);
    // Legacy owner offset signs may be inverted across older data shapes; accept either.
    if (optionOffset === parsed || optionOffset === -parsed) return option.value;
  }
  return "Europe/London";
};

const buildLegacyScheduleFromOwner = (ownerValue: any, referenceDate: Date) => {
  const periods = ownerValue?.timePeriods || {};
  const activeDays = DAY_OPTIONS.filter((day) => Boolean(periods?.[day]?.active));

  let from = "09:00";
  let to = "19:00";
  if (activeDays.length > 0) {
    const primaryDay = activeDays[0];
    const primaryPeriod = periods?.[primaryDay] || {};
    from = parseLegacyTimeToHHMM(primaryPeriod?.start) || from;
    to = parseLegacyTimeToHHMM(primaryPeriod?.end) || to;
  }

  return {
    timezone: resolveTimezoneFromLegacyOffset(ownerValue?.timeZoneOffset, referenceDate),
    from,
    to,
    days: activeDays.length > 0 ? activeDays : ["Monday"],
  };
};

export default function CampaignCreateScheduleStep() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { run: runContinue, isPending: isContinuing } = useAsyncAction();
  const { owner, currentUser, currentWorkspace } = useSelector(ownerSelector);
  const flowState = (location.state as CampaignFlowState | null) || {};
  const campaignId = resolveCampaignIdFromStateOrSearch(flowState, searchParams.get("campaignId"));
  const resolvedScope = React.useMemo(
    () =>
      resolveFlowScope({
        flowState,
        owner,
        currentUser,
        currentWorkspace,
      }),
    [flowState, owner, currentUser, currentWorkspace]
  );
  const todayStart = React.useMemo(() => getTodayStart(), []);
  const [startDate, setStartDate] = React.useState<Date | null>(
    flowState.stepFourPayload?.schedule?.startDate
      ? new Date(flowState.stepFourPayload.schedule.startDate)
      : null
  );
  const [endDate, setEndDate] = React.useState<Date | null>(
    flowState.stepFourPayload?.schedule?.endDate
      ? new Date(flowState.stepFourPayload.schedule.endDate)
      : null
  );
  const [timezone, setTimezone] = React.useState(flowState.stepFourPayload?.schedule?.timezone || "Europe/London");
  const [fromTime, setFromTime] = React.useState(flowState.stepFourPayload?.schedule?.from || "09:00");
  const [toTime, setToTime] = React.useState(flowState.stepFourPayload?.schedule?.to || "19:00");
  const [selectedDays, setSelectedDays] = React.useState<string[]>(
    flowState.stepFourPayload?.schedule?.days || ["Monday"]
  );

  React.useEffect(() => {
    const hydrateScheduleFromDraft = async () => {
      if (!campaignId) return;
      const hasStateSchedule = Boolean(flowState.stepFourPayload?.schedule);
      if (hasStateSchedule) return;
      try {
        const draftResponse = await fetchCampaignDraft({
          campaignId,
          ownerId: resolvedScope.ownerId,
          userId: resolvedScope.userId,
          workspaceId: resolvedScope.workspaceId,
        });
        const campaign = draftResponse?.campaign;
        const schedule = campaign?.schedule;
        const isCustomSchedule = String(campaign?.scheduleMode || "").toUpperCase() === "CAMPAIGN_CUSTOM";

        if (isCustomSchedule && schedule && typeof schedule === "object") {
          const nextTimezone =
            typeof schedule.timezone === "string" && schedule.timezone
              ? schedule.timezone
              : "Europe/London";
          const nextFrom =
            typeof schedule.from === "string" && schedule.from ? schedule.from : "09:00";
          const nextTo = typeof schedule.to === "string" && schedule.to ? schedule.to : "19:00";
          const nextDays =
            Array.isArray(schedule.days) && schedule.days.length > 0
              ? schedule.days.filter((day: any) => typeof day === "string")
              : ["Monday"];
          const nextStartDate = schedule.startDate ? new Date(schedule.startDate) : null;
          const nextEndDate = schedule.endDate ? new Date(schedule.endDate) : null;

          setTimezone(nextTimezone);
          setFromTime(nextFrom);
          setToTime(nextTo);
          setSelectedDays(nextDays as string[]);
          setStartDate(nextStartDate);
          setEndDate(nextEndDate);
          return;
        }

        // Legacy campaigns: prefill Step 4 from owner-level schedule settings.
        const legacySchedule = buildLegacyScheduleFromOwner(owner, new Date());
        setTimezone(legacySchedule.timezone);
        setFromTime(legacySchedule.from);
        setToTime(legacySchedule.to);
        setSelectedDays(legacySchedule.days);
      } catch (error) {
        console.log(error);
      }
    };
    void hydrateScheduleFromDraft();
  }, [
    campaignId,
    resolvedScope.ownerId,
    resolvedScope.userId,
    resolvedScope.workspaceId,
    flowState.stepFourPayload?.schedule,
    owner,
  ]);

  React.useEffect(() => {
    if (!startDate || !endDate) return;
    if (endDate.getTime() <= startDate.getTime()) {
      setEndDate(null);
    }
  }, [startDate, endDate]);

  const daysWithinSelectedRange = React.useMemo(() => {
    if (!startDate || !endDate) return DAY_OPTIONS;

    const activeDaysSet = new Set<string>();
    const cursor = new Date(startDate);
    cursor.setHours(0, 0, 0, 0);
    const end = new Date(endDate);
    end.setHours(0, 0, 0, 0);

    while (cursor.getTime() <= end.getTime()) {
      activeDaysSet.add(DAY_NAME_BY_INDEX[cursor.getDay()]);
      cursor.setDate(cursor.getDate() + 1);
    }

    return DAY_OPTIONS.filter((day) => activeDaysSet.has(day));
  }, [startDate, endDate]);

  const disabledDays = React.useMemo(
    () => DAY_OPTIONS.filter((day) => !daysWithinSelectedRange.includes(day)),
    [daysWithinSelectedRange],
  );

  React.useEffect(() => {
    setSelectedDays((prev) => {
      const filtered = prev.filter((day) => daysWithinSelectedRange.includes(day));
      if (filtered.length > 0) return filtered;
      if (daysWithinSelectedRange.length > 0) return [daysWithinSelectedRange[0]];
      return prev;
    });
  }, [daysWithinSelectedRange]);

  const handleContinue = () =>
    runContinue(async () => {
    if (!startDate) {
      toast.error("Please select a start date.");
      return;
    }
    if (!endDate) {
      toast.error("Please select an end date.");
      return;
    }
    if (endDate.getTime() <= startDate.getTime()) {
      toast.error("End date must be after start date.");
      return;
    }

    const scheduleReferenceDate = startDate || new Date();
    const stepFourPayload = {
      scheduleMode: "CAMPAIGN_CUSTOM",
      schedule: {
        timezone,
        timeZoneOffset: getTimeZoneOffsetMinutes(timezone, scheduleReferenceDate),
        from: fromTime,
        to: toTime,
        days: selectedDays,
        startDate: startDate ? startDate.toISOString() : null,
        endDate: endDate ? endDate.toISOString() : null,
      },
    };

    const loader = toast.loading("Saving changes...");
    try {
      let selectedOwnerId =
        String(flowState.stepTwoPayload?.senderOwnerId || "").trim() ||
        String(resolvedScope.ownerId || "").trim();

      // Route state can be stale/empty after reload/deep-link. Fall back to persisted draft owner.
      if (!selectedOwnerId && campaignId) {
        try {
          const draftResponse = await fetchCampaignDraft({
            campaignId,
            ownerId: resolvedScope.ownerId,
            userId: resolvedScope.userId,
            workspaceId: resolvedScope.workspaceId,
          });
          selectedOwnerId = String(draftResponse?.campaign?.ownerId || "").trim();
        } catch (e) {
          // Keep existing flow-level error handling below.
        }
      }

      if (!selectedOwnerId) {
        toast.update(loader, {
          render: "Please select a sender before scheduling.",
          type: "error",
          isLoading: false,
          autoClose: 3000,
          closeOnClick: true,
        });
        return;
      }

      const overlapCheck = await axios.post("/campaign/validateScheduleConflict", {
        campaignId,
        ownerId: selectedOwnerId,
        userId: resolvedScope.userId,
        workspaceId: resolvedScope.workspaceId,
        ...stepFourPayload,
      });
      if (overlapCheck?.data?.hasConflict) {
        const conflicts = Array.isArray(overlapCheck?.data?.conflicts)
          ? overlapCheck.data.conflicts
          : [];
        const conflictNames = conflicts
          .map((item: any) => String(item?.conflictCampaignName || "").trim())
          .filter(Boolean);
        const uniqueConflictNames = Array.from(new Set(conflictNames));
        const fallbackConflictName = String(overlapCheck?.data?.conflictCampaignName || "").trim();
        const allConflictNames = uniqueConflictNames.length
          ? uniqueConflictNames
          : fallbackConflictName
          ? [fallbackConflictName]
          : [];
        const conflictNameSuffix = allConflictNames.length
          ? ` (${allConflictNames.join(", ")})`
          : "";
        toast.update(loader, {
          render: `Schedule overlaps with another campaign${conflictNameSuffix}. Choose different days/time.`,
          type: "error",
          isLoading: false,
          autoClose: 4000,
          closeOnClick: true,
        });
        return;
      }

      const draftResponse = await upsertCampaignDraft({
        campaignId,
        ownerId: selectedOwnerId,
        userId: resolvedScope.userId,
        workspaceId: resolvedScope.workspaceId,
        ...stepFourPayload,
      });
      const nextCampaignId = draftResponse?.campaignId || campaignId;
      toast.update(loader, {
        render: "Saved successfully.",
        type: "success",
        isLoading: false,
        autoClose: 3000,
        closeOnClick: true,
      });

      navigate(
        {
          pathname: "../review-launch",
          search: draftSearchFromCampaignId(nextCampaignId),
        },
        {
          state: {
            ...flowState,
            campaignId: nextCampaignId,
            stepFourPayload,
          },
        }
      );
    } catch (error: any) {
      const apiData = error?.response?.data || {};
      const apiMessage = String(apiData?.error || "").trim();
      const conflicts = Array.isArray(apiData?.conflicts) ? apiData.conflicts : [];
      const conflictNames = conflicts
        .map((item: any) => String(item?.conflictCampaignName || "").trim())
        .filter(Boolean);
      const uniqueConflictNames = Array.from(new Set(conflictNames));
      const fallbackConflictName = String(apiData?.conflictCampaignName || "").trim();
      const allConflictNames = uniqueConflictNames.length
        ? uniqueConflictNames
        : fallbackConflictName
        ? [fallbackConflictName]
        : [];
      const conflictNameSuffix = allConflictNames.length
        ? ` (${allConflictNames.join(", ")})`
        : "";
      const isScheduleOverlapError =
        Boolean(apiData?.conflictCampaignId) ||
        /schedule overlaps with another campaign/i.test(apiMessage);

      toast.update(loader, {
        render: isScheduleOverlapError
          ? `Schedule overlaps with another campaign${conflictNameSuffix}. Choose different days/time.`
          : "Unable to save changes.",
        type: "error",
        isLoading: false,
        autoClose: 3000,
        closeOnClick: true,
      });
    }
  });

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="min-h-0 flex-1 overflow-y-auto">
      <div className="grid grid-cols-1 border-b border-light-grey px-4 py-5 md:grid-cols-[320px_1fr_1fr] md:gap-12 md:px-6">
        <div>
          <h3 className="text-md font-semibold text-dark-black">Start & end date</h3>
          <p className="mt-1 text-sm text-dark-grey">Set a start and end date for your campaign.</p>
        </div>
        <div className="mt-4 md:mt-0">
          <p className="mb-2 text-sm font-medium text-dark-black">Start date *</p>
          <DatePickerField
            value={startDate}
            onChange={setStartDate}
            size="sm"
            minDate={todayStart}
          />
        </div>
        <div className="mt-4 md:mt-0">
          <p className="mb-2 text-sm font-medium text-dark-black">End date *</p>
          <DatePickerField
            value={endDate}
            onChange={setEndDate}
            size="sm"
            disabled={!startDate}
            minDate={startDate ? new Date(startDate.getTime() + ONE_DAY_MS) : undefined}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 border-b border-light-grey px-4 py-5 md:grid-cols-[320px_1.5fr_0.8fr_0.8fr] md:gap-12 md:px-6">
        <div>
          <h3 className="text-md font-semibold text-dark-black">Active hours</h3>
          <p className="mt-1 text-sm text-dark-grey">Set a timezone and active hours for your campaign.</p>
        </div>
        <div className="mt-4 md:mt-0">
          <p className="mb-2 text-sm font-medium text-dark-black">Timezone *</p>
          <CustomSelect
            options={CAMPAIGN_TIMEZONE_OPTIONS}
            value={CAMPAIGN_TIMEZONE_OPTIONS.find((option) => option.value === timezone) || null}
            onChange={(option) => setTimezone(option?.value || "")}
            placeholder="Select timezone"
            isBorder
            isSearchable
            controlBackgroundColor="#FFFFFF"
            placeholderColor="black"
            controlMinHeight={40}
            controlBorderRadius={8}
          />
        </div>
        <div className="mt-4 md:mt-0">
          <p className="mb-2 text-sm font-medium text-dark-black">From</p>
          <Select options={HOUR_OPTIONS} value={fromTime} onChange={setFromTime} size="sm" />
        </div>
        <div className="mt-4 md:mt-0">
          <p className="mb-2 text-sm font-medium text-dark-black">To</p>
          <Select options={HOUR_OPTIONS} value={toTime} onChange={setToTime} size="sm" />
        </div>
      </div>

      <div className="grid grid-cols-1 border-b border-light-grey px-4 py-5 md:grid-cols-[320px_1fr] md:gap-12 md:px-6">
        <div>
          <h3 className="text-md font-semibold text-dark-black">Days</h3>
          <p className="mt-1 text-sm text-dark-grey">Select which days of the week you want your campaign to be active.</p>
        </div>
        <div className="mt-4 md:mt-0">
          <DaySelectionPills
            options={DAY_OPTIONS}
            value={selectedDays}
            onChange={setSelectedDays}
            disabledOptions={disabledDays}
            size="sm"
          />
        </div>
      </div>
      </div>

      <div className="shrink-0 border-t border-light-grey bg-white px-4 py-4 md:px-6">
        <div className="flex justify-end">
          <Button
            variant="primary"
            size="sm"
            iconSrc="/icons/arrow_right.svg"
            showAfter
            isLoading={isContinuing}
            onClick={handleContinue}
          >
            {isContinuing ? "Saving..." : "Continue"}
          </Button>
        </div>
      </div>
    </div>
  );
}
